/**
 * Planos e benefícios — o Dozy Premium visto de dentro.
 *
 * Este ecrã existe porque as tabelas sozinhas não chegavam: os planos nascem
 * com o preço a NULL de propósito (o documento diz que não devem ser fixados
 * antes de haver dados do piloto) e os benefícios nascem sem regra. Enquanto
 * ninguém decidir aqui, o Premium não cobra nada e não desconta nada.
 *
 * O cartão de cada plano diz-o à cabeça, em vez de deixar descobrir pela
 * ausência de subscritores.
 *
 * A taxa reduzida tem controlo próprio e não um editor de JSON. É a única
 * regra que mexe em dinheiro por serviço, e escrevê-la à mão em `{}` era
 * convidar a que um zero a mais oferecesse a margem toda.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, CreditCard, X, Check, AlertTriangle, Percent, Infinity as Infinito, Plus, Trash2,
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const kz = (v) => (v == null ? null : `${Number(v).toLocaleString('pt-PT')} Kz`);

const PERIODICIDADE = { MENSAL: 'por mês', TRIMESTRAL: 'por trimestre', ANUAL: 'por ano' };

/** A única regra com controlo dedicado — é a que mexe em dinheiro. */
const TAXA = 'TAXA_DOZY_REDUZIDA';

const PlanosPage = () => {
  const { success, error } = useToast();
  const [planos, setPlanos] = useState(null);
  const [contexto, setContexto] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [preco, setPreco] = useState('');
  const [motivo, setMotivo] = useState('');
  const [limites, setLimites] = useState({});
  const [taxaReduzida, setTaxaReduzida] = useState('');
  const [novo, setNovo] = useState('');
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get('/subscricoes/planos')
      .then((r) => setPlanos(r.data || []))
      .catch(() => { setPlanos([]); error('Não foi possível carregar os planos.'); });
  }, [error]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/subscricoes/admin/contexto').then((r) => setContexto(r.data)).catch(() => {});
  }, []);

  const abrir = (p) => {
    setAberto(p);
    setPreco(p.preco_kz ?? '');
    setMotivo('');
    setNovo('');
    setLimites(Object.fromEntries((p.beneficios || []).map((b) => [b.codigo, b.limite_mensal ?? ''])));
    const t = (p.beneficios || []).find((b) => b.codigo === TAXA);
    setTaxaReduzida(t?.regras?.taxa_cliente ?? '');
  };

  const recarregarAberto = async () => {
    const { data } = await api.get('/subscricoes/planos');
    setPlanos(data || []);
    const actual = (data || []).find((x) => x.plano_id === aberto.plano_id);
    if (actual) abrir(actual);
  };

  const gravarPlano = async () => {
    setBusy(true);
    try {
      await api.put(`/subscricoes/admin/planos/${aberto.plano_id}`, {
        preco_kz: preco === '' ? undefined : Number(preco),
        motivo: motivo.trim() || undefined,
      });
      success(`${aberto.nome}: preço gravado.`);
      setMotivo('');
      await recarregarAberto();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível gravar.'); }
    finally { setBusy(false); }
  };

  const alternarActivo = async (p) => {
    setBusy(true);
    try {
      await api.put(`/subscricoes/admin/planos/${p.plano_id}`, { activo: !p.activo });
      success(`${p.nome}: ${p.activo ? 'desactivado' : 'activado'}.`);
      carregar();
      if (aberto?.plano_id === p.plano_id) setAberto((a) => ({ ...a, activo: !p.activo }));
    } catch (e) { error(e.response?.data?.message || 'Não foi possível.'); }
    finally { setBusy(false); }
  };

  const gravarBeneficio = async (codigo, extra = {}) => {
    setBusy(true);
    try {
      const l = limites[codigo];
      await api.put(`/subscricoes/admin/planos/${aberto.plano_id}/beneficios/${codigo}`, {
        limite_mensal: l === '' || l == null ? null : Number(l),
        ...extra,
      });
      success('Benefício gravado.');
      await recarregarAberto();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível gravar.'); }
    finally { setBusy(false); }
  };

  const gravarTaxa = async () => {
    const v = taxaReduzida === '' ? null : Number(taxaReduzida);
    if (v != null && (!(v >= 0) || v >= (contexto?.taxa_cliente_normal ?? 100))) {
      return error(`A taxa reduzida tem de ser menor que a normal (${contexto?.taxa_cliente_normal}%).`);
    }
    await gravarBeneficio(TAXA, { regras: v == null ? {} : { taxa_cliente: v } });
  };

  const removerBeneficio = async (codigo) => {
    setBusy(true);
    try {
      await api.delete(`/subscricoes/admin/planos/${aberto.plano_id}/beneficios/${codigo}`);
      success('Benefício removido do plano.');
      await recarregarAberto();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível remover.'); }
    finally { setBusy(false); }
  };

  const acrescentar = async () => {
    if (!novo) return;
    setLimites((l) => ({ ...l, [novo]: '' }));
    await gravarBeneficio(novo);
    setNovo('');
  };

  /** O que falta a um plano para poder ser vendido. */
  const emFalta = (p) => {
    const f = [];
    if (p.codigo !== 'DOZY_STANDARD' && p.preco_kz == null) f.push('preço');
    if (!p.activo) f.push('activação');
    return f;
  };

  const porVertical = (v) => (planos || []).filter((p) => p.vertical === v);
  const jaNoPlano = new Set((aberto?.beneficios || []).map((b) => b.codigo));
  const disponiveis = (contexto?.beneficios || []).filter((b) => b.activo && !jaNoPlano.has(b.codigo));

  const cartao = (p) => {
    const falta = emFalta(p);
    return (
      <li key={p.plano_id}>
        <button onClick={() => abrir(p)}
          className="w-full text-left p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:bg-slate-900">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold text-slate-100">{p.nome}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">{p.codigo}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold tabular-nums text-slate-100">
                {p.preco_kz == null
                  ? <span className="text-slate-600 text-sm font-normal">sem preço</span>
                  : kz(p.preco_kz)}
              </div>
              {p.periodicidade && (
                <div className="text-[11px] text-slate-500">{PERIODICIDADE[p.periodicidade] || p.periodicidade}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
            <span className="text-slate-500">
              {p.beneficios.length} {p.beneficios.length === 1 ? 'benefício' : 'benefícios'}
            </span>
            <span className={p.activo ? 'text-green-400' : 'text-slate-500'}>
              {p.activo ? 'Activo' : 'Inactivo'}
            </span>
            {falta.length > 0 && (
              <span className="flex items-center gap-1 text-amber-400 ml-auto">
                <AlertTriangle className="w-3 h-3" /> não se vende — falta {falta.join(' e ')}
              </span>
            )}
          </div>
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-blue-400" /> Planos e benefícios
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Os planos nascem sem preço e os benefícios sem regra. Até serem decididos aqui, o Premium não cobra nem desconta nada.
        </p>
      </div>

      {planos === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : (
        <>
          <section>
            <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Dozy Home</h2>
            <ul className="space-y-2">{porVertical('HOME').map(cartao)}</ul>
          </section>
          <section>
            <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Dozy Business</h2>
            <ul className="space-y-2">{porVertical('BIZ').map(cartao)}</ul>
          </section>
        </>
      )}

      {aberto && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setAberto(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-5 my-8 space-y-5">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white">{aberto.nome}</h2>
                <p className="text-sm text-slate-500 font-mono">{aberto.codigo}</p>
              </div>
              <button onClick={() => setAberto(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {aberto.codigo === 'DOZY_STANDARD' ? (
              <p className="text-sm text-slate-400 p-3 rounded-xl bg-slate-800/50">
                O Standard é o plano por omissão e é gratuito — não tem preço nem se subscreve.
                Serve para dar benefícios a quem não paga.
              </p>
            ) : (
              <div>
                <label htmlFor="preco" className="block text-xs text-slate-400 mb-1">
                  Preço {aberto.periodicidade ? PERIODICIDADE[aberto.periodicidade] : ''} (Kz)
                </label>
                <div className="flex gap-2">
                  <input id="preco" type="number" min="0" inputMode="numeric" value={preco}
                    onChange={(e) => setPreco(e.target.value)} placeholder="Ainda por decidir"
                    className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 tabular-nums outline-none focus:border-blue-500" />
                  <button onClick={gravarPlano}
                    disabled={busy || (String(preco) !== String(aberto.preco_kz ?? '') && !motivo.trim())}
                    className="px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                    Gravar
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Sem preço, o plano não se vende. O documento recomenda fixá-lo só com dados do piloto.
                </p>
                {/* RN-004: uma alteração a dados críticos exige motivo. É o
                    único dos cinco campos que o sistema não consegue
                    reconstruir sozinho daqui a um ano. */}
                {String(preco) !== String(aberto.preco_kz ?? '') && (
                  <div className="mt-2">
                    <label htmlFor="motivo" className="block text-xs text-slate-400 mb-1">
                      Motivo da alteração <span className="text-red-400">*</span>
                    </label>
                    <input id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Ex.: Preço de lançamento do piloto Zango/Kilamba"
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm outline-none focus:border-blue-500" />
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50">
              <div>
                <div className="text-sm font-medium text-slate-200">Plano activo</div>
                <div className="text-[11px] text-slate-500">Inactivo não aparece a quem quer subscrever.</div>
              </div>
              <button onClick={() => alternarActivo(aberto)} disabled={busy}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  aberto.activo ? 'bg-slate-700 text-slate-200' : 'bg-green-600 text-white'}`}>
                {aberto.activo ? 'Desactivar' : 'Activar'}
              </button>
            </div>

            {/* ── Taxa reduzida ── */}
            {jaNoPlano.has(TAXA) && (
              <div className="p-3 rounded-xl border border-slate-700 bg-slate-950">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-200 mb-1">
                  <Percent className="w-4 h-4 text-blue-400" /> Taxa Dozy reduzida
                </h3>
                <p className="text-[11px] text-slate-500 mb-2">
                  A taxa normal é <strong className="text-slate-300 tabular-nums">{contexto?.taxa_cliente_normal ?? '—'}%</strong>.
                  O desconto sai da margem da Dozy — o prestador continua a receber os mesmos termos.
                  Em branco, não há desconto nenhum.
                </p>
                <div className="flex gap-2">
                  <input type="number" min="0" step="0.5" inputMode="decimal" value={taxaReduzida}
                    onChange={(e) => setTaxaReduzida(e.target.value)}
                    aria-label="Taxa reduzida em percentagem"
                    placeholder="Sem desconto"
                    className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 tabular-nums outline-none focus:border-blue-500" />
                  <button onClick={gravarTaxa} disabled={busy}
                    className="px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                    Gravar
                  </button>
                </div>
                {taxaReduzida !== '' && contexto?.taxa_cliente_normal != null
                  && Number(taxaReduzida) < contexto.taxa_cliente_normal && (
                  <p className="text-[11px] text-green-400 mt-2 tabular-nums">
                    Num serviço de 500.000 Kz: taxa passa de {kz(500000 * contexto.taxa_cliente_normal / 100)}
                    {' '}para {kz(500000 * Number(taxaReduzida) / 100)} — a Dozy oferece
                    {' '}{kz(500000 * (contexto.taxa_cliente_normal - Number(taxaReduzida)) / 100)}.
                  </p>
                )}
              </div>
            )}

            {/* ── Benefícios ── */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                O que este plano inclui
              </h3>
              {aberto.beneficios.length === 0 ? (
                <p className="text-xs text-slate-500 mb-2">Nenhum — este plano não dá direito a nada.</p>
              ) : (
                <ul className="space-y-1.5">
                  {aberto.beneficios.map((b) => (
                    <li key={b.codigo} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-800/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{b.nome}</div>
                        <div className="text-[11px] text-slate-600 font-mono truncate">{b.codigo}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input type="number" min="0" inputMode="numeric"
                          value={limites[b.codigo] ?? ''}
                          onChange={(e) => setLimites((l) => ({ ...l, [b.codigo]: e.target.value }))}
                          onBlur={() => gravarBeneficio(b.codigo,
                            b.codigo === TAXA
                              ? { regras: taxaReduzida === '' ? {} : { taxa_cliente: Number(taxaReduzida) } }
                              : {})}
                          aria-label={`Limite mensal de ${b.nome}`}
                          placeholder="∞"
                          className="w-16 px-2 py-1.5 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm text-center tabular-nums outline-none focus:border-blue-500" />
                        <span className="text-[11px] text-slate-600 w-10">
                          {limites[b.codigo] === '' || limites[b.codigo] == null
                            ? <Infinito className="w-3.5 h-3.5 inline" />
                            : '/mês'}
                        </span>
                        <button onClick={() => removerBeneficio(b.codigo)} disabled={busy}
                          aria-label={`Remover ${b.nome}`}
                          className="grid place-items-center w-8 h-8 rounded-md text-slate-500 hover:text-red-400 hover:bg-slate-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-slate-500 mt-2">
                Em branco é ilimitado, não zero. Escrever 0 desliga o benefício na prática.
              </p>

              {disponiveis.length > 0 && (
                <div className="flex gap-2 mt-3">
                  <select value={novo} onChange={(e) => setNovo(e.target.value)}
                    aria-label="Benefício a acrescentar"
                    className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm">
                    <option value="">Acrescentar benefício…</option>
                    {disponiveis.map((b) => <option key={b.codigo} value={b.codigo}>{b.nome}</option>)}
                  </select>
                  <button onClick={acrescentar} disabled={busy || !novo}
                    className="px-4 rounded-lg bg-slate-800 text-blue-400 text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> Juntar
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => setAberto(null)}
              className="w-full min-h-[44px] rounded-lg bg-slate-800 text-slate-300 font-medium inline-flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanosPage;
